import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authApiService } from "../../api/api";
import FilterBlock from "../../components/FilterBlock/FilterBlock";
import AddWarehouseModal from "../../components/modals/AddWarehouseModal/AddWarehouseModal";
import EditWarehouseModal from "../../components/modals/EditWarehouseModal/EditWarehouseModal";
import WarehouseFilterModal from "../../components/modals/filters/WarehouseFilterModal/WarehouseFilterModal";
import { SelectOption } from "../../components/modals/forms/inputs/SelectInput/SelectInput";
import Pagination from "../../components/pagination/Pagination/Pagination";
import WarehouseTable from "../../components/Tables/WarehouseTable";
import TableTitle from "../../components/TableTitle/TableTitle";
import { IBrand } from "../../types/brands.type";
import { IProduct } from "../../types/product.type";
import { IProductType } from "../../types/productType.type";
import { usersPerPage } from "../../utils/constants/page";
import { filterWarehouse } from "../../utils/filterUsers";
import styles from "./styles.module.scss";

const WarehousePage: React.FC = () => {
  const [warehouse, setWarehouse] = useState<IProduct[]>([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState<IProduct[]>([]);
  const [editSelectedWarehouse, setEditSelectedWarehouse] =
    useState<IProduct | null>(null);
  const [visibleWarehouses, setVisibleWarehouses] = useState<IProduct[]>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isAddWarehouseModal, setIsAddWarehouseModal] =
    useState<boolean>(false);
  const [isInfoModal, setInfoModal] = useState<boolean>(false);
  const [isFilterModal, setIsFilterModal] = useState<boolean>(false);

  const [brands, setBrands] = useState<IBrand[]>([]);
  const [selectBrand, setSelectBrand] = useState<SelectOption>({
    value: "none",
    label: "all",
  });
  const [types, setTypes] = useState<IProductType[]>([]);
  const [selectType, setSelectType] = useState<SelectOption>({
    value: "none",
    label: "all",
  });
  const [selectStartDate, setSelectStartDate] = useState<Date | null>(null);
  const [selectEndDate, setSelectEndDate] = useState<Date | null>(null);
  const [selectedWarehouses, setSelectedWarehouse] = useState<IProduct[]>([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clientId = searchParams.get("item");
  const navigate = useNavigate();

  const removeQueryParam = (key: string) => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has(key)) {
      searchParams.delete(key);
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );
    }
  };

  const getWarehouse = async () => {
    try {
      const results = await authApiService.getAllProducts();
      return results;
    } catch (err) {
      console.error(err);
    }
  };
  const getBrands = async () => {
    try {
      const results = await authApiService.getAllBrands();
      return results;
    } catch (err) {
      console.error(err);
    }
  };

  const getTypes = async () => {
    try {
      const results = await authApiService.getAllProductTypes();
      return results;
    } catch (err) {
      console.error(err);
    }
  };

  const changeVisibiltyWarehouse = (resources: IProduct[]) => {
    const indexOfLastUser = activePage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    if (warehouse) {
      const changedWarehouse = resources.slice(
        indexOfFirstUser,
        indexOfLastUser
      );

      setVisibleWarehouses(
        changedWarehouse.filter((item) => item.batches.length > 0)
      );
    }
  };

  const getOptionsFilter = (
    brand: SelectOption,
    type: SelectOption,
    startDate: Date | null,
    endDate: Date | null
  ) => {
    let filteredWarehouse = [...warehouse];
    setSelectBrand(brand);
    setSelectType(type);
    setSelectStartDate(startDate);
    setSelectEndDate(endDate);

    if (brand.value !== "none") {
      filteredWarehouse = filteredWarehouse.filter(
        (item) => item.brand.id === brand.value
      );
    }

    if (type.value !== "none") {
      filteredWarehouse = filteredWarehouse.filter(
        (item) => item.productType.id === type.value
      );
    }

    if (startDate) {
      filteredWarehouse = filteredWarehouse.filter((item) =>
        item.batches.some(
          (batch) => new Date(batch.batchDate) >= new Date(startDate)
        )
      );
    }

    if (endDate) {
      filteredWarehouse = filteredWarehouse.filter((item) =>
        item.batches.some(
          (batch) => new Date(batch.batchDate) <= new Date(endDate)
        )
      );
    }

    return filteredWarehouse;
  };

  const onFilterChange = (
    brand: SelectOption,
    type: SelectOption,
    startDate: Date | null,
    endDate: Date | null
  ) => {
    const data = getOptionsFilter(brand, type, startDate, endDate);
    const filteredUsers = filterWarehouse(data, searchValue);
    setFilteredWarehouses(filteredUsers);
  };

  const getFiltredWarehouse = (warehouse: IProduct[]) => {
    const data = getOptionsFilter(
      selectBrand,
      selectType,
      selectStartDate,
      selectEndDate
    );
    const filtered = filterWarehouse(data, searchValue);
    setFilteredWarehouses(filtered);
  };

  const onClickApplyModal = async () => {
    const resp = await getWarehouse();
    if (resp) {
      const products = resp.filter((item) => item.batches.length > 0);
      setWarehouse(products);
      getFiltredWarehouse(products);
      if (editSelectedWarehouse) {
        const item = products.find(
          (item) => item.id === editSelectedWarehouse.id
        );

        if (item) {
          setEditSelectedWarehouse(item);
        }
      }
    }
  };

  useEffect(() => {
    setTotalPages(Math.ceil(filteredWarehouses.length / usersPerPage));
  }, [filteredWarehouses]);

  useEffect(() => {
    getFiltredWarehouse(warehouse);
    // setActivePage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, warehouse]);

  useEffect(() => {
    (async () => {
      const data = await getWarehouse();
      if (data) {
        setSelectedWarehouse(data);
        setWarehouse(data.filter((item) => item.batches.length > 0));
      }

      const br = await getBrands();
      if (br) {
        setBrands(br);
      }

      const tp = await getTypes();
      if (tp) {
        setTypes(tp);
      }

      if (clientId) {
        const item = data?.find((res) => res.id.toString() === clientId);
        if (item) {
          setInfoModal(true);
          setEditSelectedWarehouse(item);
          removeQueryParam("item");
        }
      }
    })();
  }, []);

  useEffect(() => {
    changeVisibiltyWarehouse(filteredWarehouses);
    // setTotalPages(Math.ceil(users.length / usersPerPage));
    if (activePage > totalPages) {
      if (activePage === 1) {
        setActivePage(1);
      } else {
        setActivePage(totalPages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, warehouse, totalPages, filteredWarehouses]);
  return (
    <main className={styles.container}>
      <div className={styles.header}>
        <TableTitle title="Всього" count={warehouse?.length || 0} />
        <div>
          <FilterBlock
            searchValue={searchValue}
            onSearchChange={(value) => setSearchValue(value)}
            onPressAdd={() => setIsAddWarehouseModal(true)}
            onPressModal={() => setIsFilterModal(true)}
            addButtonTitle="Додати"
          />
        </div>
      </div>
      <WarehouseTable
        products={visibleWarehouses}
        callBackInfo={setInfoModal}
        callBackEdit={setEditSelectedWarehouse}
      />

      <Pagination
        key={totalPages}
        count={totalPages}
        activePage={activePage}
        onClick={(page) => setActivePage(page)}
        onClickNext={() => setActivePage((prevState) => (prevState += 1))}
        onClickPrev={() => setActivePage((prevState) => (prevState -= 1))}
      />
      {isAddWarehouseModal && (
        <AddWarehouseModal
          warehouse={selectedWarehouses}
          onClose={() => setIsAddWarehouseModal(false)}
          onApply={onClickApplyModal}
        />
      )}
      {editSelectedWarehouse && (
        <EditWarehouseModal
          warehouse={editSelectedWarehouse}
          isInfo={isInfoModal}
          onClose={() => setEditSelectedWarehouse(null)}
          onApply={onClickApplyModal}
        />
      )}
      {isFilterModal && (
        <WarehouseFilterModal
          defaultType={selectType}
          defaultBrand={selectBrand}
          onClose={() => setIsFilterModal(false)}
          onApply={onFilterChange}
          types={types}
          brands={brands}
          defaultEndDate={selectEndDate}
          defaultStartDate={selectStartDate}
        />
      )}
    </main>
  );
};

export default WarehousePage;
